import { TrendingUp, TrendingDown, DollarSign } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { computeETFMetrics } from '@etfUtils';
import {ETFHolding} from "@/integrations/supabase/etf.models.ts";

interface ETFMetricsCardsProps {
  holdings: ETFHolding[];
  prices: Record<string, ETFPriceResponse>;
  isLoading: boolean;
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

const FREQUENCY_MULTIPLIER: Record<ETFDividendFrequency, number> = {
  monthly: 12,
  quarterly: 4,
  semi_annual: 2,
  annual: 1,
};

export function computePortfolioMetrics(
  holdings: ETFHolding[],
  prices: Record<string, ETFPriceResponse>
) {
  let totalValue = 0;
  let totalAnnualYield = 0;
  let totalReturn = 0;
  let totalCostBasis = 0;

  for (const holding of holdings) {
    const priceData = prices[holding.ticker_symbol];
    const currentPrice = priceData?.price ?? 0;
    const metrics = computeETFMetrics(holding, currentPrice);
    totalValue += metrics.marketValue;
    totalAnnualYield += metrics.annualYield;
    totalReturn += metrics.totalReturn;
    totalCostBasis += metrics.costBasis;
  }

  const totalReturnPct = totalCostBasis > 0 ? (totalReturn / totalCostBasis) * 100 : 0;

  return { totalValue, totalAnnualYield, totalReturn, totalReturnPct };
}

function MetricCardSkeleton() {
  return (
    <Card>
      <CardHeader className="pb-2">
        <Skeleton className="h-4 w-28" />
      </CardHeader>
      <CardContent>
        <Skeleton className="h-8 w-32" />
      </CardContent>
    </Card>
  );
}

export function ETFMetricsCards({ holdings, prices, isLoading }: ETFMetricsCardsProps) {
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" role="status" aria-label="Loading metrics">
        <MetricCardSkeleton />
        <MetricCardSkeleton />
        <MetricCardSkeleton />
      </div>
    );
  }

  const { totalValue, totalAnnualYield, totalReturn, totalReturnPct } = computePortfolioMetrics(
    holdings,
    prices
  );

  const isPositiveReturn = totalReturn >= 0;

  return (
    <div
      className="grid grid-cols-1 sm:grid-cols-3 gap-4"
      role="region"
      aria-label="ETF portfolio metrics"
    >
      {/* Total Value */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">Total Value</CardTitle>
          <DollarSign className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold" data-testid="total-value">
            {formatCurrency(totalValue)}
          </div>
        </CardContent>
      </Card>

      {/* Est. Annual Yield */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            Est. Annual Yield
          </CardTitle>
          <TrendingUp className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold" data-testid="annual-yield">
            {formatCurrency(totalAnnualYield)}
          </div>
        </CardContent>
      </Card>

      {/* Total Return */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">Total Return</CardTitle>
          {isPositiveReturn ? (
            <TrendingUp className="h-4 w-4 text-emerald-600" aria-hidden="true" />
          ) : (
            <TrendingDown className="h-4 w-4 text-red-600" aria-hidden="true" />
          )}
        </CardHeader>
        <CardContent>
          <div
            className={`text-2xl font-bold ${isPositiveReturn ? 'text-emerald-600' : 'text-red-600'}`}
            data-testid="total-return"
          >
            {formatCurrency(totalReturn)}
            <span className="text-sm font-normal ml-1">({totalReturnPct.toFixed(2)}%)</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
